import React, { useMemo } from "react";

import * as d3 from "d3";
import { useIntl } from "react-intl";

import { formatTimeFrame } from "src/organization/pages/project/environment/metrics/components/timeframeControl/util";
import {
  ChartInfo,
  HttpLayout
} from "src/organization/pages/project/environment/metrics/http/components";
import {
  getDefaultDomain,
  getLinearAxis,
  formatNumber,
  useCursorDataSync
} from "src/organization/pages/project/environment/metrics/http/helper";
import useUpdateDimension from "src/organization/pages/project/environment/metrics/http/hook/useUpdateDimension";
import * as S from "src/organization/pages/project/environment/metrics/http/style";
import { TimeFrameType } from "src/organization/pages/project/environment/metrics/resources/types";
import { getXTickCount } from "src/organization/pages/project/environment/metrics/utils/helper";

import { useServerCachesMetricsContext } from "./context";
import Line from "./Line";
import ServerCachesTooltip from "./ServerCachesTooltip";
import { ServerCachesMetricsType } from "./types";

const chartId = "server-caches-overview";
const itemSpacing = 17;

const getDomains = (
  cachesData: ServerCachesMetricsType[][],
  timeframe?: TimeFrameType
) => {
  const yMax = d3.max(cachesData, dd => d3.max(dd, d => d?.values));

  const defaultDomain = getDefaultDomain(timeframe);
  const domain = {
    xDomain: defaultDomain.xDomain,
    yDomain: [0, yMax]
  };
  return cachesData.length ? domain : defaultDomain;
};

type CachesChartProps = {
  cachesData: ServerCachesMetricsType[][];
};

const ServerCachesOverviewChart = (props: CachesChartProps) => {
  const { timeframe } = useServerCachesMetricsContext() || {};

  const intl = useIntl();

  const dimension = useUpdateDimension("server-caches-overview-wrapper");

  const { cachesData } = props;

  const { xDomain, yDomain } = useMemo(
    () => getDomains(cachesData, timeframe),
    [timeframe, cachesData]
  );

  const { xScale, yScale, positions } = getLinearAxis({
    dimension,
    xDomain,
    yDomain
  });

  const tickX = getXTickCount(dimension.width, timeframe, itemSpacing);

  const disable = !cachesData.length;

  const completeCaches = cachesData.reduce(
    (max, next) => (next.length > max.length ? next : max),
    []
  );

  const cursorFuncProps = useMemo(
    () => ({
      xScale,
      yScale,
      data: completeCaches,
      bisectFn: d => d.date,
      disable,
      chartId
    }),
    [completeCaches, disable, xScale, yScale]
  );

  const { cursorSync, handleCursorMove } = useCursorDataSync({
    cursorFuncProps
  });

  return (
    <>
      <S.ChartTitleContainer>
        <S.ChartTitleList>
          <S.ChartTitle tag="h3">
            {intl.formatMessage({ id: "server_caches" })}
          </S.ChartTitle>
          <ChartInfo
            title={intl.formatMessage({ id: "server_caches" })}
            description={intl.formatMessage({ id: "server_caches.info" })}
          />
          <S.ChartTitleLight className="light">
            {formatTimeFrame({ id: "1h" })}
          </S.ChartTitleLight>
        </S.ChartTitleList>
      </S.ChartTitleContainer>
      <HttpLayout
        chartId={chartId}
        xScale={xScale}
        yScale={yScale}
        positions={positions}
        cursorData={cursorSync?.adaptedCursor}
        onMouseMove={handleCursorMove}
        tick={{
          x: tickX,
          y: {
            format: (value: number) => formatNumber({ value, precision: 2 })
          }
        }}
        onMouseLeave={() => handleCursorMove?.(null)}
      >
        {cachesData.map((data, i) => (
          <Line index={i} xScale={xScale} yScale={yScale} key={i} data={data} />
        ))}
      </HttpLayout>
      <ServerCachesTooltip
        enable={!!cursorSync?.adaptedCursor}
        cursorData={cursorSync?.adaptedCursor}
        series={cachesData}
      />
    </>
  );
};

export default ServerCachesOverviewChart;
